import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import API from '../api/axios';
import './CollegeManagement.css';

const LIMIT = 10;

const CollegeManagement = () => {
  const { user } = useContext(AuthContext);

  const [colleges, setColleges]               = useState([]);
  const [adminsByCollege, setAdminsByCollege]  = useState({});
  const [expandedColleges, setExpandedColleges] = useState(new Set());
  const [showCreateCollege, setShowCreateCollege] = useState(false);
  const [newCollege, setNewCollege] = useState({
    college_name: '', location: '', contact_email: '', contact_phone: ''
  });
  const [page, setPage]         = useState(1);
  const [total, setTotal]       = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const initialized = useRef(false);

  const fetchColleges = useCallback(async (p) => {
    try {
      const { data } = await API.get(
        `/public/colleges?university_id=${user.university_id}&page=${p}&limit=${LIMIT}`
      );
      setColleges(data.colleges || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      setError('Failed to fetch colleges');
    }
  }, [user.university_id]);

  const fetchCollegeAdmins = useCallback(async () => {
    try {
      if (!user.university_id) return;
      const { data } = await API.get('/public/get-college-admins', {
        params: { university_id: user.university_id }
      });
      if (data.success) {
        const grouped = {};
        for (const admin of data.collegeAdmins) {
          const key = admin.college_id;
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(admin);
        }
        setAdminsByCollege(grouped);
      }
    } catch {
      console.error('Failed to fetch college admins');
    }
  }, [user.university_id]);

  // Initial load: both in parallel
  useEffect(() => {
    Promise.all([fetchColleges(1), fetchCollegeAdmins()]).finally(() => setLoading(false));
    initialized.current = true;
  }, [fetchColleges, fetchCollegeAdmins]);

  // Page changes after mount
  useEffect(() => {
    if (!initialized.current) return;
    fetchColleges(page);
  }, [page, fetchColleges]);

  const handleCreateCollege = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      const { data } = await API.post('/auth/create-college', {
        ...newCollege,
        university_id: user.university_id
      });
      if (data.success) {
        setSuccess('College created successfully!');
        setNewCollege({ college_name: '', location: '', contact_email: '', contact_phone: '' });
        setShowCreateCollege(false);
        fetchColleges(page);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create college');
    }
  };

  const updateCollegeStatus = async (collegeId, newStatus) => {
    setError(''); setSuccess('');
    try {
      const { data } = await API.patch(`/colleges/${collegeId}/status`, { status: newStatus });
      if (data.success) { setSuccess(data.message); fetchColleges(page); }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update college status');
    }
  };

  const toggleExpand = (collegeId) => {
    setExpandedColleges(prev => {
      const next = new Set(prev);
      if (next.has(collegeId)) next.delete(collegeId);
      else next.add(collegeId);
      return next;
    });
  };

  const statusColor = {
    active: 'var(--success)',
    inactive: 'var(--warning)',
    suspended: 'var(--danger)',
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  const totalAdmins = Object.values(adminsByCollege).reduce((sum, a) => sum + a.length, 0);

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">University Management</h1>
        <p className="dashboard-subtitle">Manage colleges and their administrators</p>
      </div>

      {error   && <div className="form-error">{error}</div>}
      {success && <div className="form-success">{success}</div>}

      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Colleges</h2>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {total} college{total !== 1 ? 's' : ''} · {totalAdmins} administrator{totalAdmins !== 1 ? 's' : ''}
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowCreateCollege(true)}>
            + Create College
          </button>
        </div>

        {/* Create college modal */}
        {showCreateCollege && (
          <div className="modal-overlay" onClick={() => setShowCreateCollege(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h3>Create New College</h3>
              <form onSubmit={handleCreateCollege}>
                {[
                  ['College Name *', 'college_name', 'text',  true],
                  ['Location',       'location',     'text',  false],
                  ['Contact Email',  'contact_email','email', false],
                  ['Contact Phone',  'contact_phone','tel',   false],
                ].map(([label, key, type, req]) => (
                  <div key={key} className="form-group">
                    <label className="form-label">{label}</label>
                    <input
                      type={type}
                      className="form-input"
                      value={newCollege[key]}
                      required={req}
                      onChange={e => setNewCollege({ ...newCollege, [key]: e.target.value })}
                    />
                  </div>
                ))}
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCreateCollege(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Create College</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="table-container">
          {colleges.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">No colleges found. Click "Create College" to add one.</div>
            </div>
          ) : (
            <table className="table cm-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }} />
                  <th>College Name</th>
                  <th>Location</th>
                  <th>Contact Email</th>
                  <th>Contact Phone</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {colleges.map(college => {
                  const admins = adminsByCollege[college.college_id] || [];
                  const isOpen = expandedColleges.has(college.college_id);

                  return (
                    <>
                      {/* College row */}
                      <tr
                        key={college.college_id}
                        className={`cm-college-row${isOpen ? ' cm-college-row--open' : ''}`}
                      >
                        <td>
                          <button
                            className="cm-expand-btn"
                            onClick={() => toggleExpand(college.college_id)}
                            title={isOpen ? 'Collapse admins' : 'Expand admins'}
                            aria-expanded={isOpen}
                          >
                            <span className={`cm-chevron${isOpen ? ' cm-chevron--open' : ''}`}>›</span>
                          </button>
                        </td>
                        <td>
                          <div className="cm-college-name">{college.college_name}</div>
                          {admins.length > 0 && (
                            <div className="cm-admin-count">
                              {admins.length} admin{admins.length !== 1 ? 's' : ''}
                            </div>
                          )}
                        </td>
                        <td style={{ color: 'var(--text-secondary)' }}>{college.location || '—'}</td>
                        <td>
                          {college.contact_email
                            ? <a href={`mailto:${college.contact_email}`} className="cm-link">{college.contact_email}</a>
                            : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                        </td>
                        <td>
                          {college.contact_phone
                            ? <a href={`tel:${college.contact_phone}`} className="cm-link">{college.contact_phone}</a>
                            : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                        </td>
                        <td>
                          <select
                            className="cm-status-select"
                            value={college.status}
                            onChange={e => updateCollegeStatus(college.college_id, e.target.value)}
                            style={{ background: statusColor[college.status] || 'var(--primary)' }}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="suspended">Suspended</option>
                          </select>
                        </td>
                      </tr>

                      {/* Expanded admins row */}
                      {isOpen && (
                        <tr key={`${college.college_id}-admins`} className="cm-admin-row">
                          <td />
                          <td colSpan={5}>
                            <div className="cm-admin-panel">
                              <div className="cm-admin-panel-header">
                                College Administrators
                                <span className="cm-admin-panel-count">{admins.length}</span>
                              </div>

                              {admins.length === 0 ? (
                                <div className="cm-no-admins">No administrators assigned to this college</div>
                              ) : (
                                <div className="cm-admin-list">
                                  {admins.map(admin => (
                                    <div key={admin.user_id} className="cm-admin-card">
                                      <div className="cm-admin-avatar">
                                        {admin.name.charAt(0).toUpperCase()}
                                      </div>
                                      <div className="cm-admin-info">
                                        <div className="cm-admin-name">{admin.name}</div>
                                        {admin.designation && (
                                          <div className="cm-admin-designation">{admin.designation}</div>
                                        )}
                                        <div className="cm-admin-meta">
                                          <a href={`mailto:${admin.email}`} className="cm-link cm-admin-email">
                                            {admin.email}
                                          </a>
                                          {admin.phone && (
                                            <a href={`tel:${admin.phone}`} className="cm-link cm-admin-phone">
                                              {admin.phone}
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                      <div className="cm-admin-right">
                                        <span className={`cm-status-badge cm-status-badge--${admin.status}`}>
                                          {admin.status}
                                        </span>
                                        <div className="cm-admin-joined">
                                          Joined {new Date(admin.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="cm-pagination">
            <button
              className="btn btn-secondary btn-sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              Prev
            </button>
            <span className="cm-page-info">Page {page} of {totalPages} · {total} colleges</span>
            <button
              className="btn btn-secondary btn-sm"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollegeManagement;
